import { useCallback, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "../contexts/SnackbarContext";
import { ensureCatalog, getCatalog } from "../redux/companyCatalogs";

const announcedErrors = new Map();

export default function useCompanyCatalog(name, { owner = false, enabled = true } = {}) {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const epoch = useSelector((state) => state.catalogSession?.epoch || 0);
  const spec = getCatalog(name);
  const slice = useSelector((state) => (spec ? spec.select(state) : null));
  const items = slice?.[spec?.listKey] || [];
  const status = slice?.status || "idle";
  const error = slice?.error || null;

  useEffect(() => {
    if (!enabled || !spec) return;
    dispatch(ensureCatalog(name, { force: owner }));
  }, [dispatch, enabled, owner, name, epoch, spec]);

  const announced = useRef(false);
  useEffect(() => {
    if (!error) {
      announced.current = false;
      return;
    }
    if (announcedErrors.get(name) === error) return;
    announcedErrors.set(name, error);
    announced.current = true;
    showSnackbar(error);
  }, [error, name, showSnackbar]);

  const reload = useCallback(
    () => dispatch(ensureCatalog(name, { force: true })),
    [dispatch, name],
  );

  return {
    items,
    status,
    error,
    reload,
    loading: enabled && (status === "idle" || status === "loading"),
  };
}

export function CatalogFallback({ status, error, reload, children }) {
  if (status === "idle" || status === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {error || "Could not load this list."}
        </Typography>
        {reload && (
          <Button variant="outlined" onClick={reload}>
            Retry
          </Button>
        )}
      </Box>
    );
  }

  return children;
}
